import { SYSTEM_ADMIN_TENANT_UUID } from '@broadlume/shellcore';
/**
 * PermissionAPI.ts
 *
 * Decodes JWT ID token claims and fetches per-company feature-level permissions
 * from the backend API. Results are written directly into the Zustand permission store.
 */

import { User } from '../auth/User';
import { PluginAPI, TenantPluginFeature } from './Plugin';
import { usePermissionStore } from '../store/permissionStore';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Query keys for permission data (for cache invalidation / dependency tracking)
// ---------------------------------------------------------------------------

export const permissionKeys = {
  all: ['permissions'] as const,
  /** Per-company feature codes — key includes companyId. */
  companyFeatures: (companyId: string) =>
    [...permissionKeys.all, 'companyFeatures', companyId] as const,
};

// ---------------------------------------------------------------------------
// JWT decoding (client-side — no signature verification; backend already validated)
// ---------------------------------------------------------------------------

/** Base64url decode helper. */
const base64UrlDecode = (str: string): string => {
  // Replace URL-safe characters with standard base64
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  // Pad to multiple of 4
  const padLength = b64.length % 4;
  if (padLength) {
    b64 += '='.repeat(4 - padLength);
  }
  return decodeURIComponent(
    Array.from(
      atob(b64),
      (char: string): string => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`,
    ).join(''),
  );
};

/** Shape of a Cognito ID token payload (coarse-grained claims only). */
interface JwtPayload {
  adminLevel?: string | number;
  roleScopes?: unknown;
}

/**
 * Decode the ID token payload and extract adminLevel + roleScopes.
 */
export const decodeJwtClaims = (
  idToken: string,
): ReturnType<typeof usePermissionStore.getState>['jwtClaims'] => {
  if (!idToken || typeof idToken !== 'string') return null;

  try {
    const segments = idToken.trim().split('.');
    if (segments.length !== 3) return null;

    const parsed: unknown = JSON.parse(base64UrlDecode(segments[1]));
    // Ensure the decoded payload is a plain object (not array, primitive, or null).
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const payload = parsed as JwtPayload;

    const adminLevel: number = Number(payload.adminLevel ?? 0);
    if (isNaN(adminLevel)) return null;

    // roleScopes can be a comma-separated string or an array of strings.
    let roleScopesList: string[] = [];
    if (typeof payload.roleScopes === 'string') {
      const splitResult: ReadonlyArray<string> = payload.roleScopes.split(',');
      for (let idx = 0; idx < splitResult.length; idx += 1) {
        const trimmed = splitResult[idx].trim();
        if (trimmed.length > 0) {
          roleScopesList.push(trimmed);
        }
      }
    } else if (Array.isArray(payload.roleScopes)) {
      for (let idx2 = 0; idx2 < payload.roleScopes.length; idx2 += 1) {
        const value: unknown = payload.roleScopes[idx2];
        if (typeof value === 'string' && value.length > 0) {
          roleScopesList.push(value);
        }
      }
    }

    return { adminLevel, roleScopes: roleScopesList };
  } catch {
    console.warn('[PermissionAPI] Failed to decode JWT claims from ID token');
    return null;
  }
};

// ---------------------------------------------------------------------------
// Per-company feature permission fetching — with deduplication cache
// ---------------------------------------------------------------------------

/** In-flight guard per company — prevents duplicate concurrent requests. */
const _permissionLoadingGuards = new Map<string, Promise<void>>();

/** Timestamp of the last successful load per company (stale threshold: 5 min). */
const _lastLoadTime = new Map<string, number>();

/** Feature code prefix to avoid collisions with internal codes. */
export const FEATURE_PREFIX = 'blx:';

/**
 * Remove orphaned 3-segment base codes whose feature name matches features
 * that returned empty V3 permissions (meaning no granular actions were granted).
 * These base codes can never match hasFeature() since it requires exactly 4 segments.
 * @returns The number of codes removed.
 */
export const cleanupOrphanedBaseCodes = (
  codes: Set<string>,
  emptyPermissionFeatureNames: ReadonlySet<string>,
): number => {
  let removedCount = 0;
  for (const code of codes) {
    const parts = code.split(':');
    if (parts.length === 3 && emptyPermissionFeatureNames.has(parts[2])) {
      codes.delete(code);
      removedCount++;
    }
  }
  return removedCount;
};

/** Valid action suffixes for granular permission checks (used by fallback scanning). */
const ACTION_SUFFIXES = ['view', 'manage', 'delete'] as const;

/** Validate that a string is a well-formed feature code (e.g., "blx:cms:view"). */
export const isValidFeatureCode = (code: string): boolean =>
  typeof code === 'string' && code.startsWith(FEATURE_PREFIX) && code.split(':').length >= 3;

/**
 * Normalize a plugin identifier for use in feature codes.
 * - Prefers shellRoute (e.g., "cms", "report-viewer") — this is the URL path slug, stable and concise.
 * - Falls back to pluginName if shellRoute is absent.
 * - Replaces "/" with "_" so nested routes like "admin/users" become "admin_users".
 */
const normalizePluginKey = (pluginName: string, shellRoute?: string): string => {
  const source = shellRoute ?? pluginName;
  return source.replace(/\//g, '_');
};

/**
 * Check whether any action variant of a base code exists in the feature set.
 * Used as fallback when checking hasFeature/canManage without an explicit action suffix.
 */
const hasAnyActionVariant = (features: Set<string>, baseCode: string): boolean => {
  for (const action of ACTION_SUFFIXES) {
    if (features.has(`${baseCode}:${action}`)) return true;
  }
  return false;
};

/**
 * Merge granular permissions into a feature code set.
 * Given base codes already in the set and V3 granular data, generates
 * action-specific codes (e.g., "blx:cms-local:Posts:view") and removes
 * superseded bare base codes.
 * Also creates new 3-segment base codes when V3 returns a feature with no matching existing code.
 */
export const mergeGranularPermissions = (
  existingCodes: Set<string>,
  granularFeatures: {
    id: string;
    name: string;
    description?: string;
    permissions: string[];
    shellRoute?: string;
  }[],
): { addedCount: number; removedBaseCodes: number; orphanedCount: number } => {
  let addedCount = 0;
  const baseCodesConsumed = new Set<string>();

  for (const feature of granularFeatures) {
    if (!feature.permissions || feature.permissions.length === 0) continue;

    // Find matching base code by searching for any entry that ends with this feature name
    let baseCode: string | undefined;
    for (const code of existingCodes) {
      if (code.endsWith(`${feature.shellRoute}:${feature.name}`)) {
        baseCode = code;
        break;
      }
    }

    // If no matching base code exists, create one from the V3 response data.
    // This handles cases where a feature has granular permissions but wasn't explicitly enabled in V2.
    if (!baseCode) {
      const shellRoute = feature.shellRoute ?? '';
      const pluginKey = normalizePluginKey('', shellRoute);
      baseCode = `${FEATURE_PREFIX}${pluginKey}:${feature.name}`;
      existingCodes.add(baseCode);
    }

    baseCodesConsumed.add(baseCode);

    for (const action of feature.permissions) {
      const granularCode = `${baseCode}:${action}`;
      existingCodes.add(granularCode);
      addedCount += 1;
    }
  }

  // Remove superseded base codes that have been replaced by granular entries.
  for (const consumed of baseCodesConsumed) {
    existingCodes.delete(consumed);
  }

  return { addedCount, removedBaseCodes: baseCodesConsumed.size, orphanedCount: 0 };
};

/**
 * Convert a TenantPluginFeature array into a Set of feature code strings (base codes only),
 * then write them into the Zustand store for the given company.
 */
const parseTenantFeaturesAndStore = (companyId: string, features: TenantPluginFeature[]): void => {
  const enabledCodes: Set<string> = new Set();

  for (const tf of features) {
    if (!tf.isEnabled) continue;

    const pluginName: string = tf.tenantDetails?.pluginName ?? '';
    const featureName: string = tf.featureDetails?.name ?? '';
    const shellRoute: string | undefined = tf.tenantDetails?.shellRoute;

    // Skip entries with missing data — warn once per company to avoid spam.
    if (!pluginName || !featureName) {
      console.warn(
        `[PermissionAPI] Skipping malformed feature entry for company ${companyId}:`,
        JSON.stringify(tf),
      );
      continue;
    }

    // Build the feature code: "blx:{shellRoute}:{featureName}"  (e.g., "blx:cms:Blogs")
    const pluginKey = normalizePluginKey(pluginName, shellRoute);
    const code: string = `${FEATURE_PREFIX}${pluginKey}:${featureName}`;
    enabledCodes.add(code);
  }

  usePermissionStore.getState().setFeaturesForCompany(companyId, enabledCodes);
};

/**
 * Parse V3 granular permissions (features + granted actions) and merge them into the store.
 * Generates action-specific codes like "blx:cms-local:Posts:view", "blx:cms-local:Posts:delete".
 *
 * Also cleans up orphaned base codes — features that have a 3-segment base code but V3
 * returned empty permissions (meaning no granular actions were granted). These base codes
 * can never match hasFeature() since it requires exactly 4 segments.
 */
const parseGranularPermissionsAndStore = async ({
  companyId,
  user,
}: {
  companyId: string;
  user: User;
}): Promise<void> => {
  try {
    const featuresWithActions = await PluginAPI.getCompanyPluginFeaturesWithPermissions(
      user,
      companyId,
    );

    if (featuresWithActions.length === 0) {
      return;
    }

    const store = usePermissionStore.getState();
    const existingCodes: Set<string> = store.featuresByCompany.get(companyId) ?? new Set();

    // Use shared helper — eliminates duplicate logic between imperative and declarative paths.
    const { addedCount, removedBaseCodes } = mergeGranularPermissions(
      existingCodes,
      featuresWithActions,
    );

    if (addedCount > 0 || removedBaseCodes > 0) {
      store.setFeaturesForCompany(companyId, existingCodes);
    }

    // Clean up orphaned base codes: features with empty V3 permissions.
    const featureNamesWithEmptyPermissions = new Set(
      featuresWithActions
        .filter((f) => !f.permissions || f.permissions.length === 0)
        .map((f) => f.name),
    );
    if (featureNamesWithEmptyPermissions.size > 0) {
      const orphanedCount = cleanupOrphanedBaseCodes(
        existingCodes,
        featureNamesWithEmptyPermissions,
      );
      if (orphanedCount > 0) {
        store.setFeaturesForCompany(companyId, existingCodes);
      }
    }
  } catch (err) {
    console.warn(
      `[PermissionAPI] Failed to load granular permissions for company ${companyId}:`,
      err,
    );
    // Don't throw — base V2 features are still valid if V3 fails.
  }
};

// ---------------------------------------------------------------------------
// React Query integration — cached permission fetching with Zustand sync
// ---------------------------------------------------------------------------

import { useQuery } from '@tanstack/react-query';

/** Shape of the parsed permission data returned by this module. */
export interface PermissionData {
  /** Set of feature codes for this company (e.g., "blx:cms-local:Posts:view"). */
  features: Set<string>;
  /** Whether data is currently being fetched from the backend. */
  isLoading: boolean;
  /** Whether a fetch error occurred. */
  isError: boolean;
  /** The last error encountered (null if no errors). */
  error: Error | null;
}

/**
 * Fetch and cache per-company plugin features + granular permissions.
 * Automatically syncs the result into Zustand when data changes or arrives.
 *
 * Query key: `['permissions', 'companyFeatures', companyId]`
 * Invalidates on: companyId change, manual queryClient.invalidateQueries()
 */
export const usePermissionQuery = ({
  companyId = SYSTEM_ADMIN_TENANT_UUID,
  user,
}: {
  companyId: string;
  user: User;
}): PermissionData => {
  // Fetch base features from V2 API (cached by React Query)
  const {
    data: baseFeatures,
    isLoading: baseLoading,
    error: baseError,
  } = useQuery({
    queryKey: permissionKeys.companyFeatures(companyId),
    queryFn: async (): Promise<TenantPluginFeature[]> => {
      return PluginAPI.getCompanyPluginFeatures(user, companyId);
    },
    enabled: !!companyId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes — permissions don't change every request
    gcTime: 10 * 60 * 1000, // keep cache alive for 10 min after last use
  });

  // Fetch granular permissions from V3 API (cached independently)
  const {
    data: granularFeatures,
    isLoading: granularLoading,
    error: granularError,
  } = useQuery({
    queryKey: [...permissionKeys.all, 'granular', companyId] as const,
    queryFn: async (): Promise<
      {
        id: string;
        name: string;
        description?: string;
        permissions: string[];
        shellRoute?: string;
      }[]
    > => {
      return PluginAPI.getCompanyPluginFeaturesWithPermissions(user, companyId);
    },
    enabled: !!companyId && !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Sync parsed results into Zustand once BOTH V2 and V3 have settled.
  // granularLoading in the dep array ensures this re-runs when V3 finishes after V2.
  useEffect(() => {
    if (!companyId || !baseFeatures) return;
    // Wait until V3 has also settled (data or error) before marking loaded.
    if (granularLoading) return;

    const store = usePermissionStore.getState();

    // Step 1: Parse base features from V2 response
    parseTenantFeaturesAndStore(companyId, baseFeatures);

    // Step 2: Merge granular permissions from V3 response (if available).
    if (granularFeatures && Array.isArray(granularFeatures) && granularFeatures.length > 0) {
      // console.log(
      //   `[PermissionAPI] usePermissionQuery V3 data (${granularFeatures.length} features):`,
      //   JSON.stringify(
      //     granularFeatures.map((f) => ({
      //       name: f.name,
      //       shellRoute: f.shellRoute,
      //       permissions: f.permissions,
      //     })),
      //     null,
      //     2,
      //   ),
      // );

      const existingCodes = store.featuresByCompany.get(companyId) ?? new Set();

      // Use shared helper — same logic as parseGranularPermissionsAndStore.
      const { addedCount, removedBaseCodes } = mergeGranularPermissions(
        existingCodes,
        granularFeatures,
      );

      if (addedCount > 0 || removedBaseCodes > 0) {
        store.setFeaturesForCompany(companyId, existingCodes);
      }
    }

    // Both V2 and V3 have now settled — mark permissions loaded.
    const markLoaded = usePermissionStore.getState();
    if (!markLoaded.permissionsLoaded) {
      markLoaded.markPermissionsLoaded();
    }
  }, [companyId, baseFeatures, granularFeatures, granularLoading]);

  // Return the current feature set from Zustand (reactive to store changes).
  const featuresSet = companyId
    ? usePermissionStore((s) => s.featuresByCompany.get(companyId))
    : undefined;

  return {
    features: featuresSet ?? new Set(),
    isLoading: baseLoading || granularLoading,
    isError: !!baseError || !!granularError,
    error: baseError ?? granularError,
  };
};

// ---------------------------------------------------------------------------
// Public API (requires User for authenticated requests) — legacy imperative path
// ---------------------------------------------------------------------------

/**
 * Fetch per-company plugin features from the backend and populate Zustand store.
 * This loads both base feature codes AND granular action-level permissions.
 *
 * Used by AuthProvider lifecycle events (mount, token refresh, company switch) where
 * hooks don't apply. For component-level caching with React Query deduplication, use
 * `usePermissionQuery` instead. Both paths write to the same Zustand store so results
 * are consistent regardless of which loading path is used.
 *
 * NOTE: Uses an in-flight guard per company — concurrent calls for the same companyId
 * share a single request. Subsequent calls return immediately if data was loaded within
 * the last 5 minutes (permissions rarely change mid-session).
 */
export const loadPermissionsForCompany = async ({
  companyId = SYSTEM_ADMIN_TENANT_UUID,
  user,
}: {
  companyId: string;
  user: User;
}): Promise<void> => {
  // Fast path: skip if data was loaded within the last 5 minutes.
  const lastLoad = _lastLoadTime.get(companyId);
  if (lastLoad && Date.now() - lastLoad < 5 * 60 * 1000) return;

  // Deduplication guard: if a request is already in-flight for this company,
  // share it instead of making another pair of API calls.
  const existingGuard = _permissionLoadingGuards.get(companyId);
  if (existingGuard) {
    await existingGuard;
    return;
  }

  const guard = (async () => {
    // Signal to all consumers (AuthenticatedRoute, PermissionLoadingGuard, etc.) that
    // fresh data is being fetched — they should show a loader until markPermissionsLoaded fires.
    usePermissionStore.getState().resetPermissionsLoaded();

    try {
      // Step 1: Load base features from V2 (e.g., "blx:cms-local:Posts")
      const features: TenantPluginFeature[] = await PluginAPI.getCompanyPluginFeatures(
        user,
        companyId,
      );
      parseTenantFeaturesAndStore(companyId, features);

      // Step 2: Load granular permissions from V3 and merge action codes (e.g., "blx:cms-local:Posts:view")
      await parseGranularPermissionsAndStore({ companyId, user });

      // Both V2 and V3 have settled — mark permissions as loaded.
      usePermissionStore.getState().markPermissionsLoaded();

      // Record successful load time for deduplication cache.
      _lastLoadTime.set(companyId, Date.now());
    } catch (err) {
      console.error(`[PermissionAPI] Failed to load permissions for company ${companyId}:`, err);
      throw err; // Re-throw so AuthProvider can handle it if needed.
    } finally {
      // Clear the in-flight guard regardless of success/failure.
      _permissionLoadingGuards.delete(companyId);
    }
  })();

  _permissionLoadingGuards.set(companyId, guard);
  await guard;
};

/**
 * Decode JWT claims and write them into Zustand store.
 * Also sets currentCompanyId from the user's tenant_id attribute.
 * @param idToken - The Cognito ID token string.
 * @param companyId - Optional company/tenant ID to set as current context (from Cognito attributes).
 */
export const loadJwtClaims = (idToken: string, companyId?: string): void => {
  const claims = decodeJwtClaims(idToken);
  if (claims) {
    usePermissionStore.getState().setJwtClaims(claims);
  }
  // Set current company context so listPermissions/listPermissionsForModule can resolve.
  // Only set to a non-null value when we have a real company ID.
  // Empty string or system admin UUID → reset to null (no active company context).
  if (companyId && companyId !== '00000000-0000-0000-0000-000000000000') {
    usePermissionStore.getState().setCurrentCompanyId(companyId);
  } else {
    // Explicitly clear context when there's no valid company.
    // This handles the switch: real-company → system-admin → another-company correctly.
    usePermissionStore.getState().setCurrentCompanyId(null);
  }
};

/**
 * Invalidate the deduplication cache for a specific company, forcing a fresh load on next call.
 * Call this after explicit permission changes (e.g., admin grants/revokes access).
 */
export const invalidatePermissionsCache = (companyId: string): void => {
  _lastLoadTime.delete(companyId);
  _permissionLoadingGuards.delete(companyId);
};

/**
 * Invalidate the deduplication cache for all companies.
 * Call this on logout or tenant switch to ensure stale data is cleared.
 */
export const invalidateAllPermissionsCache = (): void => {
  _lastLoadTime.clear();
  _permissionLoadingGuards.clear();
};
